import React, { useState } from 'react';
import Navigation from './Navigation';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Separator } from './ui/separator';
import { 
  Calendar,
  Play,
  Users,
  Cpu,
  TrendingUp,
  CheckCircle,
  ArrowRight,
  Clock,
  Shield,
  BarChart3,
  Headphones,
  FileText,
  Zap,
  Target,
  X,
  Heart,
  ShoppingBag,
  PoundSterling,
  Scale,
  GraduationCap,
} from 'lucide-react';
import { useCasesData } from '../utils/useCasesData';

const UseCasesPage = () => {
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);
  const [videoKey, setVideoKey] = useState(0);

  const handleDemo = () => {
    window.open('https://cal.com/smartaisolutions/vioqbase-demo', '_blank');
  };

  const handleWatch = () => {
    setVideoKey(prev => prev + 1); // Force iframe to reload
    setIsVideoModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsVideoModalOpen(false);
    setVideoKey(prev => prev + 1); // Reset iframe when closing
  };

  return (
    <div className="min-h-screen bg-white font-sans">
      <Navigation />
      
      {/* Hero Section */}
      <section 
        className="relative bg-gradient-to-br from-[#112d56] via-[#1a3d6b] to-[#112d56] text-white py-20 lg:py-32 bg-cover bg-no-repeat"
        style={{
          backgroundImage: `url('https://customer-assets.emergentagent.com/job_voice-ai-hub-1/artifacts/x8bff3hd_teams.jpg')`,
          backgroundPosition: 'center 30%'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-[#112d56]/80 via-[#1a3d6b]/75 to-[#112d56]/80"></div>
        <div className="relative container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <div className="space-y-8">
              <div className="space-y-6">
                <h1 className="text-4xl lg:text-6xl font-bold leading-tight tracking-tight">
                  Solutions That Fit <span className="text-[#ff7a1f]">Every Team</span>
                </h1>
                <p className="text-xl lg:text-2xl text-blue-100 leading-relaxed">
                  Whether you're in HR, Operations, or Leadership — VioQbase helps your team get instant, reliable answers from your knowledge base without the noise.
                </p>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button 
                  size="lg" 
                  className="bg-[#ff7a1f] hover:bg-[#e6681b] text-white font-semibold px-8 py-6 text-lg rounded-xl transition-all duration-300 transform hover:scale-105 hover:shadow-xl"
                  onClick={handleDemo}
                >
                  <Calendar className="mr-2 h-5 w-5" />
                  Book a Demo
                </Button>
                <Button 
                  variant="outline" 
                  size="lg"
                  className="border-2 border-white text-white hover:bg-white hover:text-[#112d56] font-semibold px-8 py-6 text-lg rounded-xl transition-all duration-300"
                  onClick={handleWatch}
                >
                  <Play className="mr-2 h-5 w-5" />
                  Watch How It Works
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* HR Teams Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div className="space-y-8">
                <div className="flex items-center space-x-4 mb-6">
                  <div className="w-16 h-16 bg-gradient-to-br from-[#ff7a1f]/20 to-[#ff7a1f]/5 rounded-2xl flex items-center justify-center">
                    <Users className="h-8 w-8 text-[#ff7a1f]" />
                  </div>
                  <h2 className="text-4xl font-bold text-[#112d56]">For HR Teams</h2>
                </div>

                <div className="space-y-6">
                  <div className="space-y-4">
                    <h3 className="text-2xl font-semibold text-[#112d56]">The Challenge</h3>
                    <ul className="space-y-2">
                      {useCasesData.hrTeam.problems.map((problem, index) => (
                        <li key={index} className="flex items-start space-x-3">
                          <div className="w-2 h-2 bg-gray-400 rounded-full mt-2 flex-shrink-0"></div>
                          <span className="text-gray-700">{problem}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="space-y-4">
                    <h3 className="text-2xl font-semibold text-[#112d56]">How VioQbase Helps</h3>
                    <ul className="space-y-2">
                      {useCasesData.hrTeam.solutions.map((solution, index) => (
                        <li key={index} className="flex items-start space-x-3">
                          <CheckCircle className="h-5 w-5 text-[#ff7a1f] mt-1 flex-shrink-0" />
                          <span className="text-gray-700">{solution}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>

              <div className="space-y-8">
                <div className="relative overflow-hidden rounded-2xl shadow-lg">
                  <img 
                    src={useCasesData.hrTeam.image}
                    alt="HR team collaboration in modern office"
                    className="w-full h-48 object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#112d56]/10 to-transparent"></div>
                </div>

                <Card className="border-0 shadow-xl bg-gradient-to-br from-gray-50 to-white">
                  <CardHeader className="pb-6">
                    <CardTitle className="text-2xl font-bold text-[#112d56] flex items-center space-x-3">
                      <Zap className="h-6 w-6 text-[#ff7a1f]" />
                      <span>Transformation Results</span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {useCasesData.hrTeam.outcomes.map((outcome, index) => (
                      <div key={index} className="flex items-start space-x-3 p-3 bg-white rounded-lg">
                        <ArrowRight className="h-5 w-5 text-[#ff7a1f] mt-1 flex-shrink-0" />
                        <span className="text-gray-700 font-medium">{outcome}</span>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* IT & Operations Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div className="space-y-8 lg:order-1">
                <div className="relative overflow-hidden rounded-2xl shadow-lg">
                  <img 
                    src={useCasesData.itTeam.image}
                    alt="IT support team collaborating on solutions"
                    className="w-full h-48 object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#112d56]/10 to-transparent"></div>
                </div>
                
                <Card className="border-0 shadow-xl bg-gradient-to-br from-white to-gray-50">
                  <CardHeader className="pb-6">
                    <CardTitle className="text-2xl font-bold text-[#112d56] flex items-center space-x-3">
                      <BarChart3 className="h-6 w-6 text-[#ff7a1f]" />
                      <span>Performance Impact</span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {useCasesData.itTeam.outcomes.map((outcome, index) => (
                      <div key={index} className="flex items-start space-x-3 p-3 bg-white rounded-lg">
                        <ArrowRight className="h-5 w-5 text-[#ff7a1f] mt-1 flex-shrink-0" />
                        <span className="text-gray-700 font-medium">{outcome}</span>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              </div>

              <div className="space-y-8 lg:order-2">
                <div className="flex items-center space-x-4 mb-6">
                  <div className="w-16 h-16 bg-gradient-to-br from-[#ff7a1f]/20 to-[#ff7a1f]/5 rounded-2xl flex items-center justify-center">
                    <Cpu className="h-8 w-8 text-[#ff7a1f]" />
                  </div>
                  <h2 className="text-4xl font-bold text-[#112d56]">For IT & Operations Teams</h2>
                </div>

                <div className="space-y-6">
                  <div className="space-y-4">
                    <h3 className="text-2xl font-semibold text-[#112d56]">The Challenge</h3>
                    <ul className="space-y-2">
                      {useCasesData.itTeam.problems.map((problem, index) => (
                        <li key={index} className="flex items-start space-x-3">
                          <div className="w-2 h-2 bg-gray-400 rounded-full mt-2 flex-shrink-0"></div>
                          <span className="text-gray-700">{problem}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="space-y-4">
                    <h3 className="text-2xl font-semibold text-[#112d56]">How VioQbase Helps</h3>
                    <ul className="space-y-2">
                      {useCasesData.itTeam.solutions.map((solution, index) => (
                        <li key={index} className="flex items-start space-x-3">
                          <CheckCircle className="h-5 w-5 text-[#ff7a1f] mt-1 flex-shrink-0" />
                          <span className="text-gray-700">{solution}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Executives & Leadership Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div className="space-y-8">
                <div className="flex items-center space-x-4 mb-6">
                  <div className="w-16 h-16 bg-gradient-to-br from-[#ff7a1f]/20 to-[#ff7a1f]/5 rounded-2xl flex items-center justify-center">
                    <TrendingUp className="h-8 w-8 text-[#ff7a1f]" />
                  </div>
                  <h2 className="text-4xl font-bold text-[#112d56]">For Executives & Leadership</h2>
                </div>

                <div className="space-y-6">
                  <div className="space-y-4">
                    <h3 className="text-2xl font-semibold text-[#112d56]">The Challenge</h3>
                    <ul className="space-y-2">
                      {useCasesData.executives.problems.map((problem, index) => (
                        <li key={index} className="flex items-start space-x-3">
                          <div className="w-2 h-2 bg-gray-400 rounded-full mt-2 flex-shrink-0"></div>
                          <span className="text-gray-700">{problem}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="space-y-4">
                    <h3 className="text-2xl font-semibold text-[#112d56]">How VioQbase Helps</h3>
                    <ul className="space-y-2">
                      {useCasesData.executives.solutions.map((solution, index) => (
                        <li key={index} className="flex items-start space-x-3">
                          <CheckCircle className="h-5 w-5 text-[#ff7a1f] mt-1 flex-shrink-0" />
                          <span className="text-gray-700">{solution}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>

              <div className="space-y-8">
                <div className="relative overflow-hidden rounded-2xl shadow-lg">
                  <img 
                    src={useCasesData.executives.image}
                    alt="Business executive in professional setting"
                    className="w-full h-48 object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#112d56]/10 to-transparent"></div>
                </div>

                <Card className="border-0 shadow-xl bg-gradient-to-br from-gray-50 to-white">
                  <CardHeader className="pb-6">
                    <CardTitle className="text-2xl font-bold text-[#112d56] flex items-center space-x-3">
                      <Target className="h-6 w-6 text-[#ff7a1f]" />
                      <span>Strategic Benefits</span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {useCasesData.executives.outcomes.map((outcome, index) => (
                      <div key={index} className="flex items-start space-x-3 p-3 bg-white rounded-lg">
                        <ArrowRight className="h-5 w-5 text-[#ff7a1f] mt-1 flex-shrink-0" />
                        <span className="text-gray-700 font-medium">{outcome}</span>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Healthcare Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div className="space-y-8 lg:order-2">
                <div className="flex items-center space-x-4 mb-6">
                  <div className="w-16 h-16 bg-gradient-to-br from-[#ff7a1f]/20 to-[#ff7a1f]/5 rounded-2xl flex items-center justify-center">
                    <Heart className="h-8 w-8 text-[#ff7a1f]" />
                  </div>
                  <h2 className="text-4xl font-bold text-[#112d56]">For Healthcare</h2>
                </div>

                <div className="space-y-6">
                  <div className="space-y-4">
                    <h3 className="text-2xl font-semibold text-[#112d56]">The Challenge</h3>
                    <ul className="space-y-2">
                      {useCasesData.healthcare.problems.map((problem, index) => (
                        <li key={index} className="flex items-start space-x-3">
                          <div className="w-2 h-2 bg-gray-400 rounded-full mt-2 flex-shrink-0"></div>
                          <span className="text-gray-700">{problem}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="space-y-4">
                    <h3 className="text-2xl font-semibold text-[#112d56]">How VioQbase Helps</h3>
                    <ul className="space-y-2">
                      {useCasesData.healthcare.solutions.map((solution, index) => (
                        <li key={index} className="flex items-start space-x-3">
                          <CheckCircle className="h-5 w-5 text-[#ff7a1f] mt-1 flex-shrink-0" />
                          <span className="text-gray-700">{solution}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>

              <div className="lg:order-1 space-y-8">
                <div className="relative overflow-hidden rounded-2xl shadow-lg">
                  <img 
                    src={useCasesData.healthcare.image}
                    alt="Healthcare professional using technology"
                    className="w-full h-48 object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#112d56]/10 to-transparent"></div>
                </div>
                
                <Card className="border-0 shadow-xl bg-gradient-to-br from-white to-gray-50">
                  <CardHeader className="pb-6">
                    <CardTitle className="text-2xl font-bold text-[#112d56] flex items-center space-x-3">
                      <Heart className="h-6 w-6 text-[#ff7a1f]" />
                      <span>Healthcare Impact</span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {useCasesData.healthcare.outcomes.map((outcome, index) => (
                      <div key={index} className="flex items-start space-x-3 p-3 bg-white rounded-lg">
                        <ArrowRight className="h-5 w-5 text-[#ff7a1f] mt-1 flex-shrink-0" />
                        <span className="text-gray-700 font-medium">{outcome}</span>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Retail & Hospitality Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div className="space-y-8">
                <div className="relative overflow-hidden rounded-2xl shadow-lg">
                  <img 
                    src={useCasesData.retailHospitality.image}
                    alt="Customer service representative helping customers"
                    className="w-full h-48 object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#112d56]/10 to-transparent"></div>
                </div>
                
                <Card className="border-0 shadow-xl bg-gradient-to-br from-gray-50 to-white">
                  <CardHeader className="pb-6">
                    <CardTitle className="text-2xl font-bold text-[#112d56] flex items-center space-x-3">
                      <ShoppingBag className="h-6 w-6 text-[#ff7a1f]" />
                      <span>Customer Experience Impact</span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {useCasesData.retailHospitality.outcomes.map((outcome, index) => (
                      <div key={index} className="flex items-start space-x-3 p-3 bg-white rounded-lg">
                        <ArrowRight className="h-5 w-5 text-[#ff7a1f] mt-1 flex-shrink-0" />
                        <span className="text-gray-700 font-medium">{outcome}</span>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              </div>

              <div className="space-y-8">
                <div className="flex items-center space-x-4 mb-6">
                  <div className="w-16 h-16 bg-gradient-to-br from-[#ff7a1f]/20 to-[#ff7a1f]/5 rounded-2xl flex items-center justify-center">
                    <ShoppingBag className="h-8 w-8 text-[#ff7a1f]" />
                  </div>
                  <h2 className="text-4xl font-bold text-[#112d56]">For Retail & Hospitality</h2>
                </div>

                <div className="space-y-6">
                  <div className="space-y-4">
                    <h3 className="text-2xl font-semibold text-[#112d56]">The Challenge</h3>
                    <ul className="space-y-2">
                      {useCasesData.retailHospitality.problems.map((problem, index) => (
                        <li key={index} className="flex items-start space-x-3">
                          <div className="w-2 h-2 bg-gray-400 rounded-full mt-2 flex-shrink-0"></div>
                          <span className="text-gray-700">{problem}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="space-y-4">
                    <h3 className="text-2xl font-semibold text-[#112d56]">How VioQbase Helps</h3>
                    <ul className="space-y-2">
                      {useCasesData.retailHospitality.solutions.map((solution, index) => (
                        <li key={index} className="flex items-start space-x-3">
                          <CheckCircle className="h-5 w-5 text-[#ff7a1f] mt-1 flex-shrink-0" />
                          <span className="text-gray-700">{solution}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Finance & Payroll Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div className="space-y-8 lg:order-1">
                <div className="relative overflow-hidden rounded-2xl shadow-lg">
                  <img 
                    src={useCasesData.financePayroll.image}
                    alt="Finance and payroll office professional environment"
                    className="w-full h-48 object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#112d56]/10 to-transparent"></div>
                </div>
                
                <Card className="border-0 shadow-xl bg-gradient-to-br from-white to-gray-50">
                  <CardHeader className="pb-6">
                    <CardTitle className="text-2xl font-bold text-[#112d56] flex items-center space-x-3">
                      <PoundSterling className="h-6 w-6 text-[#ff7a1f]" />
                      <span>Financial Operations Impact</span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {useCasesData.financePayroll.outcomes.map((outcome, index) => (
                      <div key={index} className="flex items-start space-x-3 p-3 bg-white rounded-lg">
                        <ArrowRight className="h-5 w-5 text-[#ff7a1f] mt-1 flex-shrink-0" />
                        <span className="text-gray-700 font-medium">{outcome}</span>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              </div>

              <div className="space-y-8 lg:order-2">
                <div className="flex items-center space-x-4 mb-6">
                  <div className="w-16 h-16 bg-gradient-to-br from-[#ff7a1f]/20 to-[#ff7a1f]/5 rounded-2xl flex items-center justify-center">
                    <PoundSterling className="h-8 w-8 text-[#ff7a1f]" />
                  </div>
                  <h2 className="text-4xl font-bold text-[#112d56]">For Finance & Payroll</h2>
                </div>

                <div className="space-y-6">
                  <div className="space-y-4">
                    <h3 className="text-2xl font-semibold text-[#112d56]">The Challenge</h3>
                    <ul className="space-y-2">
                      {useCasesData.financePayroll.problems.map((problem, index) => (
                        <li key={index} className="flex items-start space-x-3">
                          <div className="w-2 h-2 bg-gray-400 rounded-full mt-2 flex-shrink-0"></div>
                          <span className="text-gray-700">{problem}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="space-y-4">
                    <h3 className="text-2xl font-semibold text-[#112d56]">How VioQbase Helps</h3>
                    <ul className="space-y-2">
                      {useCasesData.financePayroll.solutions.map((solution, index) => (
                        <li key={index} className="flex items-start space-x-3">
                          <CheckCircle className="h-5 w-5 text-[#ff7a1f] mt-1 flex-shrink-0" />
                          <span className="text-gray-700">{solution}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Legal & Compliance Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div className="space-y-8">
                <div className="flex items-center space-x-4 mb-6">
                  <div className="w-16 h-16 bg-gradient-to-br from-[#ff7a1f]/20 to-[#ff7a1f]/5 rounded-2xl flex items-center justify-center">
                    <Scale className="h-8 w-8 text-[#ff7a1f]" />
                  </div>
                  <h2 className="text-4xl font-bold text-[#112d56]">For Legal & Compliance</h2>
                </div>

                <div className="space-y-6">
                  <div className="space-y-4">
                    <h3 className="text-2xl font-semibold text-[#112d56]">The Challenge</h3>
                    <ul className="space-y-2">
                      {useCasesData.legalCompliance.problems.map((problem, index) => (
                        <li key={index} className="flex items-start space-x-3">
                          <div className="w-2 h-2 bg-gray-400 rounded-full mt-2 flex-shrink-0"></div>
                          <span className="text-gray-700">{problem}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="space-y-4">
                    <h3 className="text-2xl font-semibold text-[#112d56]">How VioQbase Helps</h3>
                    <ul className="space-y-2">
                      {useCasesData.legalCompliance.solutions.map((solution, index) => (
                        <li key={index} className="flex items-start space-x-3">
                          <CheckCircle className="h-5 w-5 text-[#ff7a1f] mt-1 flex-shrink-0" />
                          <span className="text-gray-700">{solution}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>

              <div className="space-y-8">
                <div className="relative overflow-hidden rounded-2xl shadow-lg">
                  <img 
                    src={useCasesData.legalCompliance.image}
                    alt="Legal and compliance professional office environment"
                    className="w-full h-48 object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#112d56]/10 to-transparent"></div>
                </div>

                <Card className="border-0 shadow-xl bg-gradient-to-br from-gray-50 to-white">
                  <CardHeader className="pb-6">
                    <CardTitle className="text-2xl font-bold text-[#112d56] flex items-center space-x-3">
                      <Scale className="h-6 w-6 text-[#ff7a1f]" />
                      <span>Compliance Excellence</span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {useCasesData.legalCompliance.outcomes.map((outcome, index) => (
                      <div key={index} className="flex items-start space-x-3 p-3 bg-white rounded-lg">
                        <ArrowRight className="h-5 w-5 text-[#ff7a1f] mt-1 flex-shrink-0" />
                        <span className="text-gray-700 font-medium">{outcome}</span>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Universities Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div className="space-y-8 lg:order-2">
                <div className="flex items-center space-x-4 mb-6">
                  <div className="w-16 h-16 bg-gradient-to-br from-[#ff7a1f]/20 to-[#ff7a1f]/5 rounded-2xl flex items-center justify-center">
                    <GraduationCap className="h-8 w-8 text-[#ff7a1f]" />
                  </div>
                  <h2 className="text-4xl font-bold text-[#112d56]">For Universities</h2>
                </div>

                <div className="space-y-6">
                  <div className="space-y-4">
                    <h3 className="text-2xl font-semibold text-[#112d56]">The Challenge</h3>
                    <ul className="space-y-2">
                      {useCasesData.universities.problems.map((problem, index) => (
                        <li key={index} className="flex items-start space-x-3">
                          <div className="w-2 h-2 bg-gray-400 rounded-full mt-2 flex-shrink-0"></div>
                          <span className="text-gray-700">{problem}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="space-y-4">
                    <h3 className="text-2xl font-semibold text-[#112d56]">How VioQbase Helps</h3>
                    <ul className="space-y-2">
                      {useCasesData.universities.solutions.map((solution, index) => (
                        <li key={index} className="flex items-start space-x-3">
                          <CheckCircle className="h-5 w-5 text-[#ff7a1f] mt-1 flex-shrink-0" />
                          <span className="text-gray-700">{solution}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>

              <div className="lg:order-1 space-y-8">
                <div className="relative overflow-hidden rounded-2xl shadow-lg">
                  <img 
                    src={useCasesData.universities.image}
                    alt="University students in academic environment"
                    className="w-full h-48 object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#112d56]/10 to-transparent"></div>
                </div>
                
                <Card className="border-0 shadow-xl bg-gradient-to-br from-white to-gray-50">
                  <CardHeader className="pb-6">
                    <CardTitle className="text-2xl font-bold text-[#112d56] flex items-center space-x-3">
                      <GraduationCap className="h-6 w-6 text-[#ff7a1f]" />
                      <span>Student Success Impact</span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {useCasesData.universities.outcomes.map((outcome, index) => (
                      <div key={index} className="flex items-start space-x-3 p-3 bg-white rounded-lg">
                        <ArrowRight className="h-5 w-5 text-[#ff7a1f] mt-1 flex-shrink-0" />
                        <span className="text-gray-700 font-medium">{outcome}</span>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Comparison Table */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl lg:text-5xl font-bold text-[#112d56] mb-6">
                Benefits Across <span className="text-[#ff7a1f]">Every Department and Industry</span>
              </h2>
              <p className="text-xl text-gray-600">
                See how VioQbase transforms workflows for different teams in your organization.
              </p>
            </div>

            <Card className="border-0 shadow-xl bg-white overflow-hidden">
              <div className="p-4">
                <table className="w-full text-sm">
                  <thead className="bg-gradient-to-r from-[#112d56] to-[#1a3d6b] text-white">
                    <tr>
                      <th className="text-left p-2 font-semibold text-xs w-32">Key Benefits</th>
                      <th className="text-center p-2 font-semibold text-xs">HR Teams</th>
                      <th className="text-center p-2 font-semibold text-xs">IT & Ops</th>
                      <th className="text-center p-2 font-semibold text-xs">Executives</th>
                      <th className="text-center p-2 font-semibold text-xs">Healthcare</th>
                      <th className="text-center p-2 font-semibold text-xs">Retail</th>
                      <th className="text-center p-2 font-semibold text-xs">Finance</th>
                      <th className="text-center p-2 font-semibold text-xs">Legal</th>
                      <th className="text-center p-2 font-semibold text-xs">Universities</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {useCasesData.comparisonTable.map((row, index) => (
                      <tr key={index} className={index % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                        <td className="p-2 font-medium text-gray-900 text-xs w-32">{row.benefit}</td>
                        <td className="p-2 text-center">
                          {row.hr ? (
                            <CheckCircle className="h-4 w-4 text-[#ff7a1f] mx-auto" />
                          ) : (
                            <div className="w-4 h-4 border-2 border-gray-300 rounded-full mx-auto"></div>
                          )}
                        </td>
                        <td className="p-2 text-center">
                          {row.it ? (
                            <CheckCircle className="h-4 w-4 text-[#ff7a1f] mx-auto" />
                          ) : (
                            <div className="w-4 h-4 border-2 border-gray-300 rounded-full mx-auto"></div>
                          )}
                        </td>
                        <td className="p-2 text-center">
                          {row.executives ? (
                            <CheckCircle className="h-4 w-4 text-[#ff7a1f] mx-auto" />
                          ) : (
                            <div className="w-4 h-4 border-2 border-gray-300 rounded-full mx-auto"></div>
                          )}
                        </td>
                        <td className="p-2 text-center">
                          {row.healthcare ? (
                            <CheckCircle className="h-4 w-4 text-[#ff7a1f] mx-auto" />
                          ) : (
                            <div className="w-4 h-4 border-2 border-gray-300 rounded-full mx-auto"></div>
                          )}
                        </td>
                        <td className="p-2 text-center">
                          {row.retail ? (
                            <CheckCircle className="h-4 w-4 text-[#ff7a1f] mx-auto" />
                          ) : (
                            <div className="w-4 h-4 border-2 border-gray-300 rounded-full mx-auto"></div>
                          )}
                        </td>
                        <td className="p-2 text-center">
                          {row.finance ? (
                            <CheckCircle className="h-4 w-4 text-[#ff7a1f] mx-auto" />
                          ) : (
                            <div className="w-4 h-4 border-2 border-gray-300 rounded-full mx-auto"></div>
                          )}
                        </td>
                        <td className="p-2 text-center">
                          {row.legal ? (
                            <CheckCircle className="h-4 w-4 text-[#ff7a1f] mx-auto" />
                          ) : (
                            <div className="w-4 h-4 border-2 border-gray-300 rounded-full mx-auto"></div>
                          )}
                        </td>
                        <td className="p-2 text-center">
                          {row.universities ? (
                            <CheckCircle className="h-4 w-4 text-[#ff7a1f] mx-auto" />
                          ) : (
                            <div className="w-4 h-4 border-2 border-gray-300 rounded-full mx-auto"></div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* Final CTA Block */}
      <section className="py-20 bg-gradient-to-r from-[#112d56] to-[#1a3d6b] text-white">
        <div className="container mx-auto px-6 text-center">
          <div className="max-w-4xl mx-auto space-y-8">
            <h2 className="text-4xl lg:text-5xl font-bold leading-tight">
              VioQbase adapts to every department — and <span className="text-[#ff7a1f]">transforms</span> the way your company runs.
            </h2>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center pt-8">
              <Button 
                size="lg"
                className="bg-[#ff7a1f] hover:bg-[#e6681b] text-white font-bold px-12 py-6 text-xl rounded-xl transition-all duration-300 transform hover:scale-105 hover:shadow-2xl"
                onClick={handleDemo}
              >
                <Calendar className="mr-3 h-6 w-6" />
                Book Your Demo
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Video Modal */}
      {isVideoModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="relative w-full max-w-4xl mx-auto">
            {/* Close Button */}
            <button
              onClick={handleCloseModal}
              className="absolute -top-12 right-0 text-white hover:text-[#ff7a1f] transition-colors z-10"
              aria-label="Close video"
            >
              <X className="h-8 w-8" />
            </button>
            
            {/* Video Container */}
            <div className="bg-white rounded-lg overflow-hidden shadow-2xl">
              <div className="bg-gradient-to-r from-[#112d56] to-[#1a3d6b] text-white p-4">
                <h3 className="text-xl font-bold">VioQbase - Watch How It Works</h3>
                <p className="text-blue-200 text-sm">See how VioQbase transforms your team's workflow</p>
              </div>
              
              {/* Vadoo.tv Embed - Raw HTML Implementation */}
              <div 
                dangerouslySetInnerHTML={{
                  __html: `<div><div style="left: 0; width: 100%; height: 0; position: relative; padding-bottom: 56.25%;"><figure style="left: 0; width: 100%; height: 0; position: relative; padding-bottom: 56.25%; margin-block-end: 0; margin-block-start: 0; margin-inline-start: 0; margin-inline-end: 0;"><iframe src="https://api.vadoo.tv/iframe_test?id=14BBjeJiKbuJKYLktJMyIiBkC3s6cVk7" scrolling="no" style="border: 0; top: 0; left: 0; width: 100%; height: 100%; position: absolute; overflow:hidden; border-radius: 5px;" allowfullscreen="1" allow="autoplay"></iframe></figure></div></div>`
                }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Footer - Added VioQbase footer */}
      <footer className="bg-[#112d56] text-white py-16">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-8 mb-12">
            <div className="md:col-span-1">
              <div className="flex items-center space-x-2 mb-6">
                <div className="w-8 h-8 bg-[#ff7a1f] rounded flex items-center justify-center">
                  <span className="text-white font-bold text-sm">V</span>
                </div>
                <span className="text-2xl font-bold">VioQbase</span>
              </div>
              <p className="text-blue-200 leading-relaxed mb-8 max-w-xs">
                Driving efficiency and customer excellence with VioSuite — AI-powered voice & knowledge solutions for modern business.
              </p>
            </div>
            
            <div>
              <h4 className="font-semibold text-xl mb-6 text-white">Product</h4>
              <ul className="space-y-3 text-blue-200">
                <li><a href="/features" className="hover:text-[#ff7a1f] transition-colors text-lg">Features</a></li>
                <li><a href="/use-cases" className="hover:text-[#ff7a1f] transition-colors text-lg">Use Cases</a></li>
                <li><a href="/products/vioreceptionist" className="hover:text-[#ff7a1f] transition-colors text-lg">VioReceptionist</a></li>
                <li><a href="/products/viocoach" className="hover:text-[#ff7a1f] transition-colors text-lg">VioCoach</a></li>
                <li><a href="https://cal.com/smartaisolutions/vioqbase-demo" target="_blank" rel="noopener noreferrer" className="hover:text-[#ff7a1f] transition-colors text-lg">Book Demo</a></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold text-xl mb-6 text-white">Company</h4>
              <ul className="space-y-3 text-blue-200">
                <li><a href="/about" className="hover:text-[#ff7a1f] transition-colors text-lg">About</a></li>
                <li><a href="/contact" className="hover:text-[#ff7a1f] transition-colors text-lg">Contact</a></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold text-xl mb-6 text-white">Legal</h4>
              <ul className="space-y-3 text-blue-200">
                <li><a href="/privacy" className="hover:text-[#ff7a1f] transition-colors text-lg">Privacy Policy</a></li>
                <li><a href="/cookies" className="hover:text-[#ff7a1f] transition-colors text-lg">Cookie Policy</a></li>
              </ul>
            </div>
          </div>
          
          <Separator className="bg-blue-800/50 my-8" />
          
          <div className="flex flex-col md:flex-row justify-between items-center">
            <p className="text-blue-200 text-lg">
              © 2025 VioQbase. All rights reserved.
            </p>
            <div className="flex space-x-8 text-blue-200 mt-4 md:mt-0">
              <a href="/privacy" className="hover:text-[#ff7a1f] transition-colors text-lg">Privacy Policy</a>
              <a href="/terms" className="hover:text-[#ff7a1f] transition-colors text-lg">Terms of Service</a>
              <a href="/cookies" className="hover:text-[#ff7a1f] transition-colors text-lg">Cookie Policy</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default UseCasesPage;